import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import {
  CustomerServiceOutlined,
  GlobalOutlined,
  LinkOutlined,
  LoginOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import eventBus from '../../utils/eventBus';
import { useUserInfo } from '../../contexts/UserInfoContext';
import './index.css';

const { Title, Paragraph, Text } = Typography;

export const INTERNATIONAL_SITE_ORIGIN = 'https://imgbed.dev';
const INTERNATIONAL_SSO_PATH = '/api/auth/sso';

interface InternationalSsoResponse {
  isSuccess?: boolean;
  action?: string;
  token?: string;
  message?: string;
}

type AccountState = 'loading' | 'anonymous' | 'annual' | 'ineligible' | 'error';

const isAllowedSsoAction = (action: string) => {
  try {
    const url = new URL(action);
    return url.origin === INTERNATIONAL_SITE_ORIGIN && url.pathname === INTERNATIONAL_SSO_PATH;
  } catch {
    return false;
  }
};

const submitSsoForm = (action: string, token: string) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;
  form.style.display = 'none';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'token';
  input.value = token;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
};

const International: React.FC = () => {
  const { t, currentLanguage } = useTranslation();
  const supportEmail = useSupportEmail();
  const { refresh, status, user } = useUserInfo();
  const [ssoLoading, setSsoLoading] = useState(false);
  const accountState: AccountState = status === 'authenticated'
    ? user?.isActiveVip === true && user.vipLevel === 'YEAR' ? 'annual' : 'ineligible'
    : status;

  const formattedVipEndTime = useMemo(() => {
    const value = user?.vipEndTime;
    if (!value) return '';
    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(currentLanguage === 'en' ? 'en-US' : 'zh-CN');
  }, [currentLanguage, user?.vipEndTime]);

  const startSso = async () => {
    setSsoLoading(true);
    try {
      const response = await fetch('/login/international-sso/token', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json().catch(() => null) as InternationalSsoResponse | null;
      if (!response.ok || !result?.isSuccess || !result.action || !result.token) {
        throw new Error(result?.message || t('international.ssoFailed'));
      }
      if (!isAllowedSsoAction(result.action)) {
        throw new Error(t('international.invalidSsoTarget'));
      }
      submitSsoForm(result.action, result.token);
    } catch (error) {
      message.error((error as Error).message || t('international.ssoFailed'));
      setSsoLoading(false);
    }
  };

  const renderAccountAction = () => {
    if (accountState === 'loading') {
      return <div className="international-account-loading"><Spin size="small" />{t('international.checkingMembership')}</div>;
    }
    if (accountState === 'error') {
      return (
        <Alert
          type="error"
          showIcon
          message={t('international.membershipCheckFailed')}
          action={<Button size="small" onClick={() => void refresh()}>{t('international.retry')}</Button>}
        />
      );
    }
    if (accountState === 'anonymous') {
      return (
        <Space direction="vertical" size="middle" className="international-account-action">
          <Alert type="info" showIcon message={t('international.loginFirstDescription')} />
          <Button
            type="primary"
            aria-label={t('international.loginFirst')}
            icon={<LoginOutlined />}
            onClick={() => eventBus.emit('openLoginModal')}
          >
            {t('international.loginFirst')}
          </Button>
        </Space>
      );
    }
    if (accountState === 'annual') {
      return (
        <Space direction="vertical" size="middle" className="international-account-action">
          <Alert
            type="success"
            showIcon
            message={t('international.annualReady')}
            description={formattedVipEndTime
              ? t('international.membershipValidUntil', { date: formattedVipEndTime })
              : t('international.annualReadyDescription')}
          />
          <Button
            type="primary"
            aria-label={t('international.oneClickLogin')}
            icon={<GlobalOutlined />}
            loading={ssoLoading}
            onClick={() => void startSso()}
          >
            {t('international.oneClickLogin')}
          </Button>
        </Space>
      );
    }
    return (
      <Space direction="vertical" size="middle" className="international-account-action">
        <Alert type="warning" showIcon message={t('international.contactDescription')} />
        <Button
          type="primary"
          aria-label={t('international.contactOwner')}
          icon={<CustomerServiceOutlined />}
          href={`mailto:${supportEmail}?subject=${encodeURIComponent(t('international.contactSubject'))}`}
        >
          {t('international.contactOwner')}
        </Button>
      </Space>
    );
  };

  return (
    <div id="international-page" className="international-page">
      <section id="international-benefits" className="international-section" aria-labelledby="international-benefits-title">
        <div className="international-section-heading">
          <Title id="international-benefits-title" level={2}>{t('international.advantagesTitle')}</Title>
        </div>
        <Row gutter={[20, 20]}>
          <Col xs={24} md={12}>
            <Card className="international-benefit-card" bordered>
              <RocketOutlined className="international-benefit-icon international-benefit-icon-speed" />
              <Title level={3}>{t('international.fasterTitle')}</Title>
              <Paragraph>{t('international.fasterDescription')}</Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="international-benefit-card" bordered>
              <SafetyCertificateOutlined className="international-benefit-icon international-benefit-icon-content" />
              <Title level={3}>{t('international.sensitiveTitle')}</Title>
              <Paragraph>{t('international.sensitiveDescription')}</Paragraph>
            </Card>
          </Col>
        </Row>
      </section>

      <section id="international-access" className="international-section" aria-labelledby="international-access-title">
        <Card className="international-access-card">
          <Row gutter={[32, 24]} align="middle">
            <Col xs={24} lg={11}>
              <Title id="international-access-title" level={2}>{t('international.accountTitle')}</Title>
              <Paragraph>{t('international.accountDescription')}</Paragraph>
              <Divider />
              <Space direction="vertical" size="small">
                <Text strong>{t('international.annualPath')}</Text>
                <Text type="secondary">{t('international.annualPathDescription')}</Text>
                <Text strong>{t('international.otherPath')}</Text>
                <Text type="secondary">{t('international.otherPathDescription')}</Text>
              </Space>
            </Col>
            <Col xs={24} lg={13}>
              <div className="international-account-panel">
                {renderAccountAction()}
                <Divider plain>{t('international.or')}</Divider>
                <Button
                  icon={<LinkOutlined />}
                  href={`${INTERNATIONAL_SITE_ORIGIN}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('international.openWithoutLogin')}
                </Button>
              </div>
            </Col>
          </Row>
        </Card>
      </section>
    </div>
  );
};

export default International;
